import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatCourseDuration, splitMinutes, formatMinutes } from '@/lib/duration'

export type DurationValue = {
  durationMinutes: number | null
  defaultDays: number
  durationLabel: string | null
}

const minutesFrom = (h: string, m: string): number | null => {
  if (h === '' && m === '') return null
  const total = (parseInt(h) || 0) * 60 + (parseInt(m) || 0)
  return total > 0 ? total : null
}

/** Total time, default days and optional website wording for a course. */
export default function CourseDurationFields({
  value, onChange,
}: {
  value: DurationValue
  onChange: (patch: Partial<DurationValue>) => void
}) {
  const total = value.durationMinutes
  // While a box is being typed in it shows exactly what was typed, so a cleared box can stay empty.
  // Once it loses focus it goes back to showing the saved value.
  const [draft, setDraft] = useState<{ h?: string; m?: string; d?: string }>({})
  const hours = draft.h ?? (total == null ? '' : String(Math.floor(total / 60)))
  const minutes = draft.m ?? (total == null ? '' : String(total % 60))
  const days = draft.d ?? String(value.defaultDays)
  const settle = () => setDraft({})

  const setTime = (h: string, m: string) => {
    setDraft(d => ({ ...d, h, m }))
    onChange({ durationMinutes: minutesFrom(h, m) })
  }

  const shownAs = formatCourseDuration(value)
  const parts = total != null && value.defaultDays > 1 ? splitMinutes(total, value.defaultDays) : null
  const evenSplit = parts && parts.every(p => p === parts[0])

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="durationHours">Duration: hours</Label>
          <Input
            id="durationHours" type="number" min="0" step="1" inputMode="numeric"
            value={hours} placeholder="e.g. 6"
            onChange={e => setTime(e.target.value, minutes)}
            onBlur={settle}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="durationMinutes">Minutes</Label>
          <Input
            id="durationMinutes" type="number" min="0" max="59" step="5" inputMode="numeric"
            value={minutes} placeholder="e.g. 30"
            onChange={e => setTime(hours, e.target.value)}
            onBlur={settle}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="defaultDays">Number of days</Label>
          <Input
            id="defaultDays" type="number" min="1" max="30" step="1" inputMode="numeric"
            value={days}
            onChange={e => {
              const raw = e.target.value
              setDraft(d => ({ ...d, d: raw }))
              const n = parseInt(raw)
              if (!Number.isNaN(n) && n >= 1) onChange({ defaultDays: Math.min(30, n) })
            }}
            onBlur={settle}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="durationLabel">Shown on your website as (optional)</Label>
        <Input
          id="durationLabel" maxLength={80}
          value={value.durationLabel ?? ''}
          onChange={e => onChange({ durationLabel: e.target.value === '' ? null : e.target.value })}
          placeholder="e.g. 1 day, or 2 half-days"
        />
      </div>

      <p className="text-xs text-gray-500">
        {shownAs ? <>Shown as <strong className="text-gray-700">{shownAs}</strong>. </> : 'No duration set. '}
        New bookings and sessions are pre-filled with {value.defaultDays === 1 ? 'one day' : `${value.defaultDays} days`}
        {total != null && parts && evenSplit ? ` of ${formatMinutes(parts[0])}` : ''}
        {total != null && value.defaultDays === 1 ? ` of ${formatMinutes(total)}` : ''}
        , and you can change them each time.
      </p>
    </div>
  )
}
